// src/components/SubirExcelSicDetalleFactura.js
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const SubirExcelSicDetalleFactura = () => {
  const [archivo, setArchivo] = useState(null);
  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [progreso, setProgreso] = useState(0);
  const [duplicados, setDuplicados] = useState([]);
  const [mostrarDuplicados, setMostrarDuplicados] = useState(false);
  const [selecciones, setSelecciones] = useState({});

  const manejarArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setMensaje('❌ Por favor, selecciona un archivo Excel (.xlsx, .xls) o CSV');
      return;
    }

    setArchivo(file);
    setMensaje('');
    setProgreso(0);
    setDuplicados([]);
    setMostrarDuplicados(false);
    setSelecciones({});

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const binaryStr = event.target.result;
        const workbook = XLSX.read(binaryStr, { type: 'binary' });

        if (workbook.SheetNames.length === 0) {
          setMensaje('❌ El archivo no contiene hojas de cálculo');
          return;
        }

        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convertir a JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setMensaje('❌ El archivo no contiene datos');
          return;
        }

        // Validar que tenga las columnas necesarias
        const primeraFila = jsonData[0];
        const columnasRequeridas = ['codigo', 'codigoComercialCodigo', 'detalle'];
        const columnasFaltantes = columnasRequeridas.filter(col => !primeraFila.hasOwnProperty(col));

        if (columnasFaltantes.length > 0) {
          setMensaje(`❌ Faltan columnas requeridas: ${columnasFaltantes.join(', ')}`);
          return;
        }

        // Encontrar duplicados en codigoComercialCodigo
        const codigosMap = {};
        const duplicadosEncontrados = [];

        jsonData.forEach((registro, index) => {
          const codigoComercial = registro.codigoComercialCodigo?.toString().trim();
          if (codigoComercial) {
            if (codigosMap[codigoComercial]) {
              codigosMap[codigoComercial].push({ ...registro, index: index + 2 }); // +2 porque Excel empieza en 1 y la primera fila es headers
            } else {
              codigosMap[codigoComercial] = [{ ...registro, index: index + 2 }];
            }
          }
        });

        // Filtrar solo los que tienen duplicados
        Object.keys(codigosMap).forEach(codigo => {
          if (codigosMap[codigo].length > 1) {
            duplicadosEncontrados.push({
              codigoComercial: codigo,
              registros: codigosMap[codigo]
            });
          }
        });

        setDuplicados(duplicadosEncontrados);

        // Inicializar selecciones para duplicados (seleccionar el primer registro por defecto)
        const nuevasSelecciones = {};
        duplicadosEncontrados.forEach(duplicado => {
          nuevasSelecciones[duplicado.codigoComercial] = 0; // Seleccionar el primer registro
        });
        setSelecciones(nuevasSelecciones);

        if (duplicadosEncontrados.length > 0) {
          setMensaje(`⚠️ Se encontraron ${duplicadosEncontrados.length} codigoComercialCodigo duplicados. Selecciona cuál registro conservar.`);
        } else {
          setMensaje(`✅ Se encontraron ${jsonData.length} registros válidos sin duplicados`);
        }

        setDatos(jsonData);

      } catch (error) {
        console.error('Error al leer archivo:', error);
        setMensaje('❌ Error al leer el archivo. Asegúrate de que es un Excel válido.');
      }
    };

    reader.onerror = () => {
      setMensaje('❌ Error al leer el archivo');
    };

    reader.readAsBinaryString(file);
  };

  const manejarSeleccionDuplicado = (codigoComercial, indice) => {
    setSelecciones(prev => ({
      ...prev,
      [codigoComercial]: indice
    }));
  };

  const obtenerDatosFiltrados = () => {
    if (duplicados.length === 0) return datos;

    // Crear un mapa con los registros seleccionados para cada código duplicado
    const registrosSeleccionados = {};
    duplicados.forEach(duplicado => {
      const indiceSeleccionado = selecciones[duplicado.codigoComercial];
      registrosSeleccionados[duplicado.codigoComercial] = duplicado.registros[indiceSeleccionado];
    });

    // Filtrar los datos originales
    return datos.filter(registro => {
      const codigoComercial = registro.codigoComercialCodigo?.toString().trim();

      // Si es un código duplicado, mantener solo el registro seleccionado
      if (duplicados.some(d => d.codigoComercial === codigoComercial)) {
        const registroSeleccionado = registrosSeleccionados[codigoComercial];
        return (
          registro.codigo === registroSeleccionado.codigo &&
          registro.detalle === registroSeleccionado.detalle
        );
      }

      // Si no es duplicado, mantener el registro
      return true;
    });
  };

  const subirDatosAFirestore = async () => {
    const datosFiltrados = obtenerDatosFiltrados();

    if (datosFiltrados.length === 0) {
      setMensaje('❌ No hay datos para subir');
      return;
    }

    setCargando(true);
    setMensaje('⏳ Subiendo datos a Firestore...');
    setProgreso(0);

    try {
      let insertados = 0;
      let actualizados = 0;
      let errores = 0;

      // Subir datos
      for (let i = 0; i < datosFiltrados.length; i++) {
        const registro = datosFiltrados[i];
        const { codigo, codigoComercialCodigo, detalle } = registro;

        // Actualizar progreso
        setProgreso(Math.round((i / datosFiltrados.length) * 100));

        // Validar campos requeridos
        if (!codigo || !codigoComercialCodigo || !detalle) {
          errores++;
          continue;
        }

        // Validar que codigoComercialCodigo no esté vacío
        const codigoComercial = codigoComercialCodigo.toString().trim();
        if (!codigoComercial) {
          errores++;
          continue;
        }

        try {
          // Usar codigoComercialCodigo como ID del documento
          const docRef = doc(db, 'SicDetalleFactura', codigoComercial);

          await setDoc(docRef, {
            codigo: codigo.toString().trim(),
            codigoComercialCodigo: codigoComercial,
            detalle: detalle.toString().trim(),
            // AQUI se agrega el nuevo campo Inventario
            Inventario: true,
            fechaActualizacion: serverTimestamp(),
            fechaCreacion: serverTimestamp(),
            metadata: {
              archivoOrigen: archivo?.name || 'desconocido',
              fechaSubida: new Date().toISOString(),
              procesado: true
            }
          }, { merge: true });

          // Verificar si fue una inserción o actualización
          const esDuplicado = duplicados.some(dup =>
            dup.codigoComercial === codigoComercial
          );

          if (esDuplicado) {
            actualizados++;
          } else {
            insertados++;
          }

        } catch (error) {
          console.error('Error subiendo registro:', error);
          errores++;
        }
      }

      setProgreso(100);
      setMensaje(`✅ ¡Completado! ${insertados} insertados, ${actualizados} actualizados, ${errores} errores`);

      // Limpiar después de subir
      setDatos([]);
      setArchivo(null);
      setDuplicados([]);
      setSelecciones({});

      // Resetear input file
      setTimeout(() => {
        const fileInput = document.getElementById('file-input-sic-detalle');
        if (fileInput) {
          fileInput.value = '';
        }
      }, 2000);

    } catch (error) {
      console.error('Error general:', error);
      setMensaje(`❌ Error al subir datos: ${error.message}`);
    } finally {
      setCargando(false);
    }
  };

  const descargarPlantilla = () => {
    try {
      const plantilla = [
        {
          codigo: 'PROD001',
          codigoComercialCodigo: 'COM001_UNICO',
          detalle: 'Producto de ejemplo 1'
        },
        {
          codigo: 'PROD002',
          codigoComercialCodigo: 'COM002_UNICO',
          detalle: 'Producto de ejemplo 2'
        },
        {
          codigo: 'PROD003',
          codigoComercialCodigo: 'COM003_UNICO',
          detalle: 'Producto de ejemplo 3'
        }
      ];

      const worksheet = XLSX.utils.json_to_sheet(plantilla);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'SicDetalleFactura');
      XLSX.writeFile(workbook, 'plantilla_sic_detalle_factura.xlsx');

      setMensaje('✅ Plantilla descargada correctamente');

    } catch (error) {
      setMensaje('❌ Error al crear plantilla');
    }
  };

  const limpiarTodo = () => {
    setDatos([]);
    setArchivo(null);
    setMensaje('');
    setProgreso(0);
    setDuplicados([]);
    setMostrarDuplicados(false);
    setSelecciones({});
    const fileInput = document.getElementById('file-input-sic-detalle');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.titulo}>📊 Subir Datos al Catálogo de Productos</h2>

      <div style={styles.instrucciones}>
        <h3>📋 Instrucciones:</h3>
        <ul style={styles.listaInstrucciones}>
          <li>El archivo Excel debe tener las columnas: <strong>codigo</strong>, <strong>codigoComercialCodigo</strong>, <strong>detalle</strong></li>
          <li>La primera fila debe contener los nombres de las columnas</li>
          <li>Los datos deben comenzar desde la segunda fila</li>
          <li>El campo <strong>codigoComercialCodigo</strong> será usado como ID único del documento</li>
          <li>Para registros duplicados, <strong>selecciona cuál conservar</strong> antes de subir</li>
        </ul>
      </div>

      <div style={styles.controles}>
        <button
          onClick={descargarPlantilla}
          style={{
            ...styles.boton,
            ...styles.botonSecundario,
            ...(cargando && styles.botonDeshabilitado)
          }}
          disabled={cargando}
        >
          📥 Descargar Plantilla
        </button>

        <div style={styles.inputContainer}>
          <label htmlFor="file-input-sic-detalle" style={styles.inputLabel}>
            📁 Seleccionar Archivo Excel
          </label>
          <input
            id="file-input-sic-detalle"
            type="file"
            accept=".xlsx, .xls, .csv"
            onChange={manejarArchivo}
            style={styles.input}
            disabled={cargando}
          />
        </div>

        {archivo && (
          <button
            onClick={limpiarTodo}
            style={{
              ...styles.boton,
              ...styles.botonPeligro
            }}
            disabled={cargando}
          >
            🗑️ Limpiar
          </button>
        )}
      </div>

      {archivo && (
        <div style={styles.infoArchivo}>
          <p><strong>📄 Archivo:</strong> {archivo.name}</p>
          <p><strong>📊 Registros:</strong> {datos.length}</p>
          <p><strong>📏 Tamaño:</strong> {(archivo.size / 1024).toFixed(2)} KB</p>
          {duplicados.length > 0 && (
            <p><strong>⚠️ Duplicados:</strong> {duplicados.length} códigos comerciales repetidos</p>
          )}
        </div>
      )}

      {cargando && (
        <div style={styles.progresoContainer}>
          <div style={styles.barraProgreso}>
            <div
              style={{
                ...styles.progreso,
                width: `${progreso}%`
              }}
            />
          </div>
          <span style={styles.textoProgreso}>{progreso}% completado</span>
        </div>
      )}

      {mensaje && (
        <div style={{
          ...styles.mensaje,
          ...(mensaje.includes('✅') ? styles.mensajeExito :
            mensaje.includes('❌') ? styles.mensajeError :
              mensaje.includes('⚠️') ? styles.mensajeAdvertencia :
                styles.mensajeInfo)
        }}>
          {mensaje}
          {duplicados.length > 0 && (
            <button
              onClick={() => setMostrarDuplicados(!mostrarDuplicados)}
              style={styles.botonVerDuplicados}
            >
              {mostrarDuplicados ? '👁️ Ocultar' : '👁️ Ver'} duplicados
            </button>
          )}
        </div>
      )}

      {mostrarDuplicados && duplicados.length > 0 && (
        <div style={styles.duplicadosContainer}>
          <h3 style={styles.duplicadosTitulo}>⚠️ Códigos Comerciales Duplicados</h3>
          <p style={styles.duplicadosInstrucciones}>
            Selecciona qué registro conservar para cada código duplicado:
          </p>
          {duplicados.map((duplicado, index) => (
            <div key={index} style={styles.duplicadoItem}>
              <h4 style={styles.duplicadoCodigo}>Código: {duplicado.codigoComercial}</h4>
              <div style={styles.tablaContainer}>
                <table style={styles.tabla}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Seleccionar</th>
                      <th style={styles.th}>Fila</th>
                      <th style={styles.th}>Código</th>
                      <th style={styles.th}>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicado.registros.map((registro, idx) => (
                      <tr
                        key={idx}
                        style={{
                          ...(idx % 2 === 0 ? styles.filaPar : styles.filaImpar),
                          ...(selecciones[duplicado.codigoComercial] === idx && styles.filaSeleccionada)
                        }}
                      >
                        <td style={styles.td}>
                          <input
                            type="radio"
                            name={`duplicado-${duplicado.codigoComercial}`}
                            checked={selecciones[duplicado.codigoComercial] === idx}
                            onChange={() => manejarSeleccionDuplicado(duplicado.codigoComercial, idx)}
                            style={styles.radio}
                          />
                        </td>
                        <td style={styles.td}>{registro.index}</td>
                        <td style={styles.td}>{registro.codigo}</td>
                        <td style={styles.td}>{registro.detalle}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {datos.length > 0 && (
        <div style={styles.vistaPrevia}>
          <h3>👀 Vista previa (primeros 5 registros):</h3>
          <div style={styles.tablaContainer}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={styles.th}>Código</th>
                  <th style={styles.th}>Código Comercial (ID)</th>
                  <th style={styles.th}>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {datos.slice(0, 5).map((fila, index) => {
                  const esDuplicado = duplicados.some(dup =>
                    dup.codigoComercial === fila.codigoComercialCodigo
                  );
                  return (
                    <tr key={index} style={esDuplicado ? styles.filaDuplicada : {}}>
                      <td style={styles.td}>{fila.codigo}</td>
                      <td style={{
                        ...styles.td,
                        fontWeight: 'bold',
                        color: esDuplicado ? '#dc2626' : '#1e40af'
                      }}>
                        {fila.codigoComercialCodigo}
                        {esDuplicado && ' ⚠️'}
                      </td>
                      <td style={styles.td}>{fila.detalle}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {datos.length > 5 && (
            <p style={styles.masRegistros}>... y {datos.length - 5} registros más</p>
          )}
        </div>
      )}

      {datos.length > 0 && (
        <button
          onClick={subirDatosAFirestore}
          style={{
            ...styles.boton,
            ...styles.botonPrincipal,
            ...(cargando && styles.botonDeshabilitado)
          }}
          disabled={cargando}
        >
          {cargando ? '⏳ Subiendo...' : '🚀 Subir a Firestore'}
        </button>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '900px',
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  },
  titulo: {
    color: '#1e40af',
    marginBottom: '20px',
    textAlign: 'center',
    fontSize: '28px',
    fontWeight: 'bold'
  },
  instrucciones: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #dee2e6'
  },
  listaInstrucciones: {
    margin: 0,
    paddingLeft: '20px',
    lineHeight: '1.6',
    color: '#495057'
  },
  controles: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#374151'
  },
  boton: {
    padding: '12px 20px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    minWidth: '140px'
  },
  botonPrincipal: {
    backgroundColor: '#10b981',
    color: 'white',
  },
  botonSecundario: {
    backgroundColor: '#3b82f6',
    color: 'white',
  },
  botonPeligro: {
    backgroundColor: '#ef4444',
    color: 'white',
  },
  botonDeshabilitado: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
    opacity: 0.6
  },
  botonVerDuplicados: {
    marginLeft: '10px',
    padding: '5px 10px',
    backgroundColor: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  input: {
    padding: '10px',
    border: '2px solid #d1d5db',
    borderRadius: '6px',
    minWidth: '250px',
    fontSize: '14px'
  },
  infoArchivo: {
    backgroundColor: '#e0f2fe',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #93c5fd'
  },
  progresoContainer: {
    marginBottom: '20px',
    textAlign: 'center'
  },
  barraProgreso: {
    width: '100%',
    height: '20px',
    backgroundColor: '#e5e7eb',
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '10px'
  },
  progreso: {
    height: '100%',
    backgroundColor: '#10b981',
    transition: 'width 0.3s ease',
    borderRadius: '10px'
  },
  textoProgreso: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#374151'
  },
  mensaje: {
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontWeight: 'bold',
    textAlign: 'center'
  },
  mensajeExito: {
    backgroundColor: '#dcfce7',
    border: '2px solid #bbf7d0',
    color: '#166534'
  },
  mensajeError: {
    backgroundColor: '#fee2e2',
    border: '2px solid #fecaca',
    color: '#dc2626'
  },
  mensajeAdvertencia: {
    backgroundColor: '#fef3c7',
    border: '2px solid #fcd34d',
    color: '#92400e'
  },
  mensajeInfo: {
    backgroundColor: '#dbeafe',
    border: '2px solid #bfdbfe',
    color: '#1e40af'
  },
  duplicadosContainer: {
    backgroundColor: '#fffbeb',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '2px solid #fcd34d'
  },
  duplicadosTitulo: {
    color: '#92400e',
    marginBottom: '15px',
    textAlign: 'center'
  },
  duplicadosInstrucciones: {
    textAlign: 'center',
    marginBottom: '20px',
    fontStyle: 'italic',
    color: '#92400e'
  },
  duplicadoItem: {
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#fef3c7',
    borderRadius: '6px',
    border: '1px solid #f59e0b'
  },
  duplicadoCodigo: {
    color: '#b45309',
    margin: '0 0 10px 0',
    fontSize: '16px'
  },
  radio: {
    transform: 'scale(1.2)',
    cursor: 'pointer'
  },
  vistaPrevia: {
    marginBottom: '20px',
    backgroundColor: '#f9fafb',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb'
  },
  tablaContainer: {
    overflowX: 'auto',
    marginBottom: '10px'
  },
  tabla: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
    backgroundColor: 'white',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  th: {
    backgroundColor: '#4f46e5',
    color: 'white',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '600'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #e5e7eb'
  },
  filaPar: {
    backgroundColor: '#f9fafb'
  },
  filaImpar: {
    backgroundColor: 'white'
  },
  filaSeleccionada: {
    backgroundColor: '#d1fae5',
    border: '2px solid #10b981'
  },
  filaDuplicada: {
    backgroundColor: '#fef3c7'
  },
  masRegistros: {
    textAlign: 'center',
    marginTop: '10px',
    fontStyle: 'italic',
    color: '#6b7280',
    fontSize: '14px'
  }
};

export default SubirExcelSicDetalleFactura;